import type { AthleteGoal, BlockPhase, LocalDate, RecoveryContext } from '@cadencia/contracts';
import { classifyChange, type ChangeClassification } from '../decisions/change-policy.js';
import { daysBetweenLocal, startOfLocalWeek } from '../units/local-date.js';
import type { PerformanceTrend } from './volume-response.js';

/**
 * Seleccion de fase y duracion del bloque
 * (docs/TRAINING_ENGINE.md §2.2 y §2.3).
 *
 * El arbol es DETERMINISTA. El documento admite que la IA proponga una fase,
 * pero quien valida es esto: una recomendacion que el arbol no respalda no se
 * aplica. Por eso vive en el dominio y no depende de nada externo.
 */

// ---------------------------------------------------------------------------
// Umbrales
// ---------------------------------------------------------------------------

export const PHASE_THRESHOLDS = {
  /** Semanas sin entrenar que obligan a readaptacion. */
  detrainedWeeks: 8,
  /**
   * Antiguedad maxima de una senal corporal para que cuente, en semanas.
   *
   * Ocho, que es el bloque mas largo (`PLANNED_WEEKS_BY_PHASE`): una medida
   * tomada hace mas de un bloque entero describe otro cuerpo.
   *
   * YA NO DECIDE NINGUNA FASE, y se queda por otra razon: el seguimiento de
   * progreso tambien necesita saber cuando una medida ha caducado, y ese
   * criterio no debe existir en dos sitios con dos numeros.
   *
   * Los umbrales `bodyFatHigh`, `bodyFatLow`, `waistToHeightHigh` y `adherence`
   * se retiraron el 21/09/2026 con el arbol que los leia. No se conservan «por
   * si acaso»: un umbral que no alimenta ninguna decision es una cifra que
   * alguien volvera a conectar sin saber de donde salio.
   */
  signalFreshnessWeeks: 8,
} as const;

/**
 * Semanas previstas por fase. CONVENCION declarada: el documento fija el rango
 * 4-8 «segun fase» pero no reparte los valores.
 */
export const PLANNED_WEEKS_BY_PHASE: Record<BlockPhase, number> = {
  readaptation: 4,
  // Heredadas: nadie las genera ya, pero un bloque guardado con este valor
  // sigue necesitando saber cuantas semanas dura.
  fat_loss: 8,
  recomposition: 6,
  muscle_gain: 8,
  // Seis y no ocho: un bloque de enfasis mas largo acumula fatiga local en los
  // mismos patrones. Ver el comentario de `specialization` en `phase-params.ts`.
  specialization: 6,
  maintenance: 4,
  // REVISAR: una transicion suele ser mas corta que el minimo de 4 del
  // documento. Se deja en 4 para no salirse del rango sin decidirlo aparte.
  transition: 4,
};

/**
 * Si la fase es la de readaptacion.
 *
 * Una linea, pero con dueño. El motor de cargas necesita saberlo en dos sitios
 * —la estrategia inicial de `defaultStrategyFor` y el cierre de sesion— y la
 * revision semanal en un tercero; con la comparacion suelta en cada uno, el dia
 * que la readaptacion deje de ser una sola fase habria que acordarse de los
 * tres. Ademas es una regla de dominio, y las pantallas no llevan reglas.
 */
export function isReadaptationPhase(phase: BlockPhase): boolean {
  return phase === 'readaptation';
}

export const MAX_EXTENSION_WEEKS = 2;
export const MAX_SHORTENING_WEEKS = 3;

// ---------------------------------------------------------------------------
// Seleccion de fase
// ---------------------------------------------------------------------------

export interface PhaseSelectionInput {
  /** Semanas sin entrenar antes de empezar. */
  weeksDetrained: number;
  /** Es el primer bloque tras un periodo de inactividad. */
  firstBlockAfterInactivity: boolean;
  /**
   * Objetivo DECLARADO por el atleta. No se deduce de ninguna medida.
   *
   * `null` = **todavia no se sabe**, que no es lo mismo que «no ha declarado
   * nada». Pasa en un dispositivo recien estrenado cuya primera bajada no ha
   * llegado: la fila puede existir en el servidor diciendo cualquier cosa. Se
   * distinguen porque el motivo que se ensena y se registra tiene que decir la
   * verdad: «mantenimiento porque lo pediste» y «mantenimiento porque aun no se
   * que pediste» son dos frases distintas, y la segunda se arregla esperando.
   */
  goal: AthleteGoal | null;
  /** Contexto de recuperacion DECLARADO. Modula; no elige el bloque. */
  recovery: RecoveryContext;
  /** El atleta ha marcado algun musculo como prioritario. */
  hasPriorityMuscles: boolean;
  /** El readiness no viene cayendo de forma sostenida. */
  readinessStable: boolean;
  /** Tendencia de e1RM en los patrones principales. */
  performance: PerformanceTrend;
  /**
   * Bloques de ACUMULACION encadenados justo antes de este, sin descarga en
   * medio. Es lo que obliga a intercalar una transicion.
   */
  consecutiveDevelopmentBlocks: number;
  previousPhase: BlockPhase | null;
}

export interface PhaseDecision {
  phase: BlockPhase;
  plannedWeeks: number;
  reasonCodes: string[];
  /** `null` si la fase no cambia: no hay nada que aprobar. */
  change: ChangeClassification | null;
}

/**
 * Bloques de desarrollo seguidos tras los cuales toca descargar.
 *
 * Tres. Con bloques de 6-8 semanas y un deload intercalado dentro de cada uno
 * (§5.1), tres seguidos son entre cuatro y seis meses de acumulacion: bastante
 * para que la fatiga que no se ve en una semana se note en un trimestre. Es
 * CONVENCION DECLARADA y esta aqui para poder discutirla en un solo sitio, no
 * un hallazgo.
 */
export const MAX_CONSECUTIVE_DEVELOPMENT_BLOCKS = 3;

/**
 * Que bloque viene despues (§6 del encargo).
 *
 * QUE CAMBIO EL 21/09/2026
 *
 * El arbol anterior decidia con TRES señales y dos eran corporales: grasa por
 * bioimpedancia, ratio cintura/altura y adherencia a un plan de comidas. De ahi
 * salian `fat_loss`, `recomposition` o `maintenance`, es decir, una decision de
 * BALANCE ENERGETICO disfrazada de decision de entrenamiento. El §6 lo prohibe
 * expresamente: «no utilices automaticamente un porcentaje de grasa, una
 * cintura o una variacion de peso para decidir una dieta o imponer una fase».
 *
 * Ahora decide con lo que el atleta DECLARA —objetivo, contexto de
 * recuperacion, prioridades— y con lo que se OBSERVA de su entrenamiento
 * —readiness y rendimiento—. El peso y los perimetros siguen registrandose y
 * siguen siendo informacion util (§5), pero ya no imponen nada.
 *
 * Y NO ES UNA SUSTITUCION MECANICA. El §11 avisa: no cambiar «adherencia
 * nutricional» por «adherencia al entrenamiento» conservando la misma logica.
 * Aqui no hay ningun umbral de adherencia: no se le niega un bloque de
 * desarrollo a nadie por haberse saltado sesiones. Saltarse sesiones cambia lo
 * que se puede progresar, no lo que se puede querer.
 *
 * MANTENER ES UNA RESPUESTA VALIDA, no el cajon de lo que no encaja: cuando el
 * objetivo declarado es mantener, este arbol contesta mantenimiento y eso no es
 * una renuncia (§11: «revisar no obliga a cambiar»).
 */
export function selectBlockPhase(input: PhaseSelectionInput): PhaseDecision {
  const decidir = (phase: BlockPhase, ...codes: string[]): PhaseDecision => ({
    phase,
    plannedWeeks: PLANNED_WEEKS_BY_PHASE[phase],
    reasonCodes: codes,
    // Cambiar de bloque es SIEMPRE cambio relevante; mantenerlo no es un cambio.
    change: input.previousPhase === phase ? null : classifyChange({ kind: 'phase_change' }),
  });

  // 1. Readaptacion antes que nada. Es la unica que no se negocia: volver tras
  //    ocho semanas parado con la carga de antes es como se rompe la gente.
  if (
    input.firstBlockAfterInactivity ||
    input.weeksDetrained > PHASE_THRESHOLDS.detrainedWeeks
  ) {
    return decidir('readaptation', 'detrained_or_first_block');
  }

  // 2. Descargar cuando se llevan varios bloques acumulando Y el cuerpo lo
  //    dice. Las DOS condiciones, no una: tres bloques seguidos con el
  //    readiness firme y la fuerza subiendo no piden ninguna descarga, y
  //    forzarla seria interrumpir lo que funciona. La fatiga de un bloque
  //    concreto ya la resuelve el deload de dentro (§5.1); esto es la otra
  //    escala.
  const acumulaDemasiado =
    input.consecutiveDevelopmentBlocks >= MAX_CONSECUTIVE_DEVELOPMENT_BLOCKS;
  if (acumulaDemasiado && (!input.readinessStable || input.performance === 'falling')) {
    return decidir('transition', 'sustained_accumulation_with_fatigue');
  }

  // 3. Recuperacion mermada POR DECLARACION del atleta. No por una bascula.
  //    Sostener el estimulo sin subirlo es lo correcto cuando alguien dice que
  //    duerme mal, esta en deficit por su cuenta o atraviesa una mala racha.
  if (input.recovery === 'reduced') {
    return decidir('maintenance', 'declared_reduced_recovery');
  }

  // 4. Sin haber podido leer el objetivo, lo prudente es sostener, y DECIRLO
  //    asi. Encadenar un bloque de desarrollo sobre un objetivo que no se ha
  //    leido gasta recuperacion contra un ajuste que puede decir otra cosa.
  if (input.goal === null) {
    return decidir('maintenance', 'goal_not_known_yet');
  }

  // 5. El objetivo declarado manda. Mantener es una eleccion, no un residuo.
  if (input.goal === 'maintenance') {
    return decidir('maintenance', 'declared_maintenance_goal');
  }

  // 6. Especializar exige las tres cosas: que haya a QUE dar enfasis, y que el
  //    atleta este en condiciones de absorberlo. Concentrar volumen sobre un
  //    readiness que cae o una fuerza que baja es añadir estimulo donde ya no
  //    se esta recuperando lo que hay.
  if (input.hasPriorityMuscles && input.readinessStable && input.performance !== 'falling') {
    return decidir('specialization', 'priorities_declared_and_recovering');
  }

  // 7. Desarrollo general. Es el bloque por defecto de esta aplicacion, y debe
  //    serlo: es un entrenador de culturismo natural.
  return decidir(
    'muscle_gain',
    input.goal === 'performance' ? 'performance_goal_general_development' : 'muscle_gain_goal',
  );
}

/**
 * La fase del PRIMER bloque, a partir de lo que el atleta declara en el test
 * inicial (docs/TEST_INICIAL.md §2.5).
 *
 * Hasta el test, el primer bloque era siempre de readaptación: la aplicación
 * no sabía nada del atleta y lo prudente era suponer que volvía de una parada.
 * Ahora lo pregunta. Quien dice que viene de una parada empieza por
 * readaptación —igual que antes—; quien entrena con regularidad empieza por lo
 * que persigue, con el mismo árbol que decide los bloques siguientes.
 *
 * Sin historial no hay rendimiento ni readiness que leer: se pasan como
 * desconocido y estable, que es lo que son al empezar.
 */
export function firstBlockPhase(input: {
  detrainedSince: LocalDate | null;
  goal: AthleteGoal;
  recovery: RecoveryContext;
  hasPriorityMuscles: boolean;
  today: LocalDate;
}): PhaseDecision {
  const weeksDetrained =
    input.detrainedSince === null
      ? 0
      : Math.max(0, Math.floor(daysBetweenLocal(input.detrainedSince, input.today) / 7));
  return selectBlockPhase({
    weeksDetrained,
    firstBlockAfterInactivity: input.detrainedSince !== null,
    goal: input.goal,
    recovery: input.recovery,
    hasPriorityMuscles: input.hasPriorityMuscles,
    readinessStable: true,
    performance: 'unknown',
    consecutiveDevelopmentBlocks: 0,
    previousPhase: null,
  });
}

// ---------------------------------------------------------------------------
// Cambiar cuando empieza un bloque
// ---------------------------------------------------------------------------

export interface RescheduleBlockInput {
  /** Lunes en el que empieza hoy el bloque. */
  currentStart: LocalDate;
  /** Lunes al que se quiere mover. */
  newStart: LocalDate;
  today: LocalDate;
  /**
   * Hay entrenamiento REGISTRADO dentro del bloque: alguna sesion empezada o
   * terminada. Una sesion perdida no cuenta: no se entreno nada.
   */
  hasLoggedWork: boolean;
}

export interface RescheduleBlockCheck {
  allowed: boolean;
  reasonCodes: string[];
}

/**
 * ¿Se puede cambiar el dia en el que empieza un bloque?
 *
 * El caso que lo motiva: se crea un bloque para probar la aplicacion, pasan las
 * semanas y la cabecera dice «semana 3 de 4» sin que se haya entrenado nada. La
 * fecha de inicio se elige al CREAR el bloque, y esa tarjeta solo aparece
 * cuando no hay ninguno activo, asi que no habia forma de volver atras.
 *
 * Se bloquea en dos casos, y los dos por el mismo motivo: **no falsear el
 * historial**.
 *
 * * Con entrenamiento registrado dentro, mover las fechas separa lo que se
 *   levanto del dia en que se levanto, y de ahi salen todas las
 *   recomendaciones. Un bloque con trabajo dentro se cierra o se termina; no se
 *   muda.
 * * Hacia atras, porque un bloque que empieza antes de esta semana fabrica
 *   sesiones perdidas que nadie perdio.
 */
export function canRescheduleBlock(input: RescheduleBlockInput): RescheduleBlockCheck {
  const reasonCodes: string[] = [];

  if (input.hasLoggedWork) reasonCodes.push('block_has_logged_work');
  if (input.newStart === input.currentStart) reasonCodes.push('same_start');
  if (input.newStart < startOfLocalWeek(input.today)) reasonCodes.push('start_in_past');

  return { allowed: reasonCodes.length === 0, reasonCodes };
}

// ---------------------------------------------------------------------------
// Duracion
// ---------------------------------------------------------------------------

export interface DurationInput {
  plannedWeeks: number;
  weeksElapsed: number;
  /** Las metricas de salida del bloque ya se cumplen. */
  exitMetricsMet: boolean;
  /** No se cumplen todavia, pero progresan. */
  exitMetricsProgressing: boolean;
  /** Fatiga sostenida detectada (deload, readiness, rendimiento). */
  sustainedFatigue: boolean;
}

export interface DurationDecision {
  weeks: number;
  deltaWeeks: number;
  reasonCodes: string[];
  change: ChangeClassification;
}

/**
 * Ajusta la duracion prevista del bloque (§2.2).
 *
 * Orden de prioridad: primero se mira si hay motivo para ACORTAR. Un bloque que
 * ya cumplio sus metricas o que acumula fatiga no debe alargarse aunque las
 * metricas sigan progresando; seguir sumando semanas sobre fatiga sostenida es
 * como se llega a una lesion por acumulacion.
 */
export function adjustBlockDuration(input: DurationInput): DurationDecision {
  const reasonCodes: string[] = [];
  let weeks = input.plannedWeeks;

  if (input.exitMetricsMet) {
    weeks = Math.max(input.weeksElapsed, input.plannedWeeks - MAX_SHORTENING_WEEKS);
    reasonCodes.push('exit_metrics_met_early');
  } else if (input.sustainedFatigue) {
    weeks = Math.max(input.weeksElapsed, input.plannedWeeks - MAX_SHORTENING_WEEKS);
    reasonCodes.push('sustained_fatigue');
  } else if (input.exitMetricsProgressing && input.weeksElapsed >= input.plannedWeeks) {
    weeks = input.plannedWeeks + MAX_EXTENSION_WEEKS;
    reasonCodes.push('metrics_progressing_not_met');
  } else {
    reasonCodes.push('duration_unchanged');
  }

  const deltaWeeks = weeks - input.plannedWeeks;

  return {
    weeks,
    deltaWeeks,
    reasonCodes,
    // El umbral esta en semanas, no en porcentaje: alargar una semana es lo
    // mismo tenga el bloque la duracion que tenga.
    change: classifyChange({ kind: 'block_duration', magnitude: Math.abs(deltaWeeks) }),
  };
}
